import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './ClientOperationAddForm.css';
import { authJsonHeaders } from '../utils/authHeaders';

const DEFAULT_OPERATION_NAMES = [
  'HONORAIRES REÇU CABINET',
  'HONORAIRES PLATEFORME TEJ',
  'HONO',
  'AVANCE',
  'ARGENT CLIENT CHEZ CGM',
  'PAYÉ PAR FOND CLIENT',
  'RC',
  'BENEF.EFFECTIF',
  'RESERVATION',
  'CNSS',
  'DECLARATION',
  'DECLARATION MENSUELLE',
  'ENREGT',
  'RNE',
  'PUBLICATION',
  'CACHET',
  'TAPEZ MANUELLE',
];

/** Libellés réservés aux opérations crédit (TAPEZ MANUELLE est commun) */
const CREDIT_OPERATION_NAMES = [
  'HONORAIRES REÇU CABINET',
  'HONORAIRES REÇU',
  'HONORAIRES PLATEFORME TEJ',
  'AVANCE',
  'ARGENT CLIENT CHEZ CGM',
];

function normalizeLibelleKey(name) {
  return String(name || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isCreditLibelle(name) {
  const key = normalizeLibelleKey(name);
  if (key === 'TAPEZ MANUELLE') return false; // commun : filtré à part
  return CREDIT_OPERATION_NAMES.some((c) => normalizeLibelleKey(c) === key)
    || key.startsWith('HONORAIRES RECU');
}

function filterOperationNames(names, side) {
  const list = Array.isArray(names) && names.length ? names : DEFAULT_OPERATION_NAMES;
  const withoutManual = list.filter((n) => normalizeLibelleKey(n) !== 'TAPEZ MANUELLE');
  const filtered =
    side === 'credit'
      ? withoutManual.filter((n) => isCreditLibelle(n))
      : withoutManual.filter((n) => !isCreditLibelle(n));
  return [...filtered, 'TAPEZ MANUELLE'];
}

const DEFAULT_CREDIT_TYPES = [
  { id: 'bank_deposit', label: 'Dépôt bancaire', fields: ['name', 'amount', 'date'] },
  { id: 'bank_draft', label: 'Traite bancaire', fields: ['reference', 'amount', 'date'] },
  { id: 'bank_transfer', label: 'Virement bancaire', fields: ['reference', 'amount', 'date'] },
  { id: 'cash', label: 'Espèces', fields: ['name', 'amount', 'date'] },
  { id: 'check', label: 'Chèque', fields: ['reference', 'amount', 'date'] },
];

const DEFAULT_DEBIT_TYPES = [
  { id: 'bank_card', label: 'Carte bancaire', fields: ['amount', 'date', 'card_id'] },
  { id: 'cash', label: 'Espèces', fields: ['amount', 'date'] },
];

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

function emptyDetail(fields, amount, date) {
  const d = {};
  fields.forEach((f) => {
    if (f === 'amount') d[f] = amount || '';
    else if (f === 'date') d[f] = date || todayDate();
    else d[f] = '';
  });
  return d;
}

/** Suffixe libellé = mode de paiement + nom/référence (espaces), toujours en MAJUSCULES */
function buildPaymentSuffix(modeLabel, detail, fields) {
  const parts = [];
  const mode = String(modeLabel || '').trim();
  if (mode) parts.push(mode);

  const fieldList = Array.isArray(fields) ? fields : [];
  if (fieldList.includes('name')) {
    const name = String(detail?.name || '').trim();
    if (name) parts.push(name);
  }
  if (fieldList.includes('reference')) {
    const ref = String(detail?.reference || '').trim();
    if (ref) parts.push(ref);
  }
  if (fieldList.includes('card_id')) {
    const card = String(detail?.card_id || '').trim();
    if (card) parts.push(card);
  }
  return parts
    .join(' ')
    .normalize('NFC')
    .toLocaleUpperCase('fr-FR');
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripTrailingToken(text, token) {
  const raw = String(text || '').trim();
  const t = String(token || '').trim();
  if (!raw || !t) return raw;
  return raw.replace(new RegExp(`\\s*${escapeRegExp(t)}\\s*$`, 'i'), '').trim();
}

/** Retire un éventuel préfixe mode déjà présent dans un texte libre */
function stripLeadingModeLabel(text, modeLabel) {
  const raw = String(text || '').trim();
  const mode = String(modeLabel || '').trim();
  if (!raw || !mode) return raw;
  const re = new RegExp(`^${escapeRegExp(mode)}\\s*`, 'i');
  return raw.replace(re, '').trim();
}

function splitOperationName(fullName, names) {
  const full = String(fullName || '').trim();
  if (!full) return { operation_name: '', operation_name_extra: '' };
  const list = (Array.isArray(names) && names.length ? names : DEFAULT_OPERATION_NAMES)
    .filter((n) => normalizeLibelleKey(n) !== 'TAPEZ MANUELLE')
    .slice()
    .sort((a, b) => b.length - a.length);
  for (const base of list) {
    const re = new RegExp(`^${escapeRegExp(base)}\\s*-?\\s*`, 'i');
    if (re.test(full)) {
      return {
        operation_name: base,
        operation_name_extra: full.replace(re, '').trim(),
      };
    }
  }
  return { operation_name: 'TAPEZ MANUELLE', operation_name_extra: full };
}

export default function ClientOperationAddForm({
  clientId,
  annee,
  isCaissier,
  getCaissierDateOptions,
  getCaissierDateValueForSelect,
  isCarteBancaire = false,
  editId = null,
  /** Solde fond client chez CGM ; si <= 0, les débits manuels sont bloqués */
  fondClientSolde = null,
  onSuccess,
  onCancel,
  onError,
}) {
  const { user } = useAuth();
  const isEdit = editId != null;
  const [meta, setMeta] = useState({
    operation_names: DEFAULT_OPERATION_NAMES,
    credit_types: DEFAULT_CREDIT_TYPES,
    debit_types: DEFAULT_DEBIT_TYPES,
  });
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(isEdit);
  const [error, setError] = useState('');

  const initialDate = isCaissier
    ? getCaissierDateOptions(annee)[0]?.value?.split('T')[0] || todayDate()
    : todayDate();

  const [form, setForm] = useState({
    operation_side: isCarteBancaire ? 'debit' : 'credit',
    date: initialDate,
    operation_name: isCarteBancaire ? 'TAPEZ MANUELLE' : '',
    operation_name_extra: isCarteBancaire ? '[CARTE BANCAIRE]' : '',
    amount: '',
    detail_type: isCarteBancaire ? 'bank_card' : 'cash',
    detail: emptyDetail(
      isCarteBancaire ? ['amount', 'date', 'card_id'] : ['name', 'amount', 'date'],
      '',
      initialDate
    ),
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const metaRes = await fetch('/api/charges-mensuelles/operation-meta');
        const metaData = await metaRes.json();
        if (!cancelled && metaData.success) {
          setMeta({
            operation_names: metaData.operation_names || DEFAULT_OPERATION_NAMES,
            credit_types: metaData.credit_types || DEFAULT_CREDIT_TYPES,
            debit_types: metaData.debit_types || DEFAULT_DEBIT_TYPES,
          });
        }
      } catch {
        /* defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      setError('');
      try {
        const res = await fetch(`/api/charges-mensuelles/${editId}/detail`);
        const data = await res.json();
        if (!res.ok || !data.success || !data.operation) {
          throw new Error(data.error || data.message || 'Impossible de charger l\'opération');
        }
        if (cancelled) return;
        const op = data.operation;
        const side = op.operation_side === 'debit' ? 'debit' : 'credit';
        const types = side === 'credit' ? (meta.credit_types.length ? meta.credit_types : DEFAULT_CREDIT_TYPES) : (meta.debit_types.length ? meta.debit_types : DEFAULT_DEBIT_TYPES);
        const detailType = op.detail_type || types[0]?.id || '';
        const typeCfg = types.find((t) => t.id === detailType) || types[0];
        const fields = typeCfg?.fields || [];
        const amountStr = op.amount != null && op.amount !== '' ? String(op.amount) : '';
        const dateStr = (op.date || todayDate()).slice(0, 10);
        const split = splitOperationName(op.operation_name, meta.operation_names);
        const detail = emptyDetail(fields, amountStr, dateStr);
        Object.keys(op.detail || {}).forEach((k) => {
          if (op.detail[k] != null && op.detail[k] !== '') detail[k] = String(op.detail[k]);
        });
        if (!detail.amount) detail.amount = amountStr;
        if (!detail.date) detail.date = dateStr;

        // Si le libellé stocké contient déjà "Mode + ref/nom", ne garder en complément
        // que le texte manuel éventuel (hors mode / détail).
        let extra = split.operation_name_extra;
        const modeLabel = typeCfg?.label || '';
        extra = stripLeadingModeLabel(extra, modeLabel);
        extra = stripTrailingToken(extra, detail.name);
        extra = stripTrailingToken(extra, detail.reference);
        extra = stripTrailingToken(extra, detail.card_id);
        extra = stripTrailingToken(extra, modeLabel);

        setForm({
          operation_side: side,
          date: dateStr,
          operation_name: split.operation_name,
          operation_name_extra: extra,
          amount: amountStr,
          detail_type: detailType,
          detail,
        });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, isEdit]);

  const detailTypes = form.operation_side === 'credit' ? meta.credit_types : meta.debit_types;
  const currentType = detailTypes.find((t) => t.id === form.detail_type);

  const fondSoldeNum =
    fondClientSolde == null || fondClientSolde === ''
      ? null
      : parseFloat(fondClientSolde);
  // Seul « PAYÉ PAR FOND CLIENT » est bloqué si fond <= 0 (ou absent)
  const canPayFromFond = Number.isFinite(fondSoldeNum) && fondSoldeNum > 0;
  const payeParFondBlockedMessage =
    'Fond client chez CGM à 0 : impossible d\'utiliser « PAYÉ PAR FOND CLIENT ». Ajoutez d\'abord un crédit (ARGENT CLIENT CHEZ CGM).';

  const isPayeParFondLibelle = (name) => {
    const key = normalizeLibelleKey(name);
    return key.includes('PAYE PAR FOND CLIENT');
  };

  const detailFields = useMemo(() => currentType?.fields || [], [currentType]);
  // Montant / date détail = déjà saisis en haut → on n'affiche que référence / nom / carte
  const visibleDetailFields = useMemo(
    () => detailFields.filter((f) => f !== 'amount' && f !== 'date'),
    [detailFields]
  );

  const availableLibelles = useMemo(
    () => filterOperationNames(meta.operation_names, form.operation_side),
    [meta.operation_names, form.operation_side]
  );

  const setField = (name, value) => {
    if (name === 'operation_name' && isPayeParFondLibelle(value) && !canPayFromFond) {
      setError(payeParFondBlockedMessage);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const setDetailField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      detail: { ...prev.detail, [name]: value },
    }));
  };

  const handleSideChange = (side) => {
    const types = side === 'credit' ? meta.credit_types : meta.debit_types;
    const first = types[0];
    const dateVal = form.date;
    const amt = form.amount;
    const nextLibelles = filterOperationNames(meta.operation_names, side);
    const keepName =
      form.operation_name &&
      nextLibelles.some((n) => normalizeLibelleKey(n) === normalizeLibelleKey(form.operation_name)) &&
      !(isPayeParFondLibelle(form.operation_name) && !canPayFromFond);
    setError('');
    setForm((prev) => ({
      ...prev,
      operation_side: side,
      operation_name: keepName ? prev.operation_name : '',
      operation_name_extra: keepName ? prev.operation_name_extra : (isCarteBancaire ? '[CARTE BANCAIRE]' : ''),
      detail_type: first?.id || '',
      detail: emptyDetail(first?.fields || [], amt, dateVal),
    }));
  };

  const handleDetailTypeChange = (typeId) => {
    const cfg = detailTypes.find((t) => t.id === typeId);
    setForm((prev) => ({
      ...prev,
      detail_type: typeId,
      detail: emptyDetail(cfg?.fields || [], prev.amount, prev.date),
    }));
  };

  const handleAmountChange = (value) => {
    setForm((prev) => ({
      ...prev,
      amount: value,
      detail: { ...prev.detail, amount: value },
    }));
  };

  const handleDateChange = (value) => {
    const dateOnly = value.includes('T') ? value.split('T')[0] : value;
    setForm((prev) => ({
      ...prev,
      date: dateOnly,
      detail: { ...prev.detail, date: dateOnly },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const amt = parseFloat(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Montant invalide.');
      setLoading(false);
      return;
    }
    if (!form.operation_name) {
      setError('Choisissez un libellé.');
      setLoading(false);
      return;
    }
    if (!form.detail_type) {
      setError('Choisissez un sous-type.');
      setLoading(false);
      return;
    }

    if (!isEdit && isPayeParFondLibelle(form.operation_name) && !canPayFromFond) {
      setError(payeParFondBlockedMessage);
      setLoading(false);
      return;
    }

    // Détails de paiement obligatoires selon le mode
    if (form.detail_type === 'bank_card') {
      const cardId = parseInt(String(form.detail.card_id || ''), 10);
      if (!Number.isFinite(cardId) || cardId <= 0) {
        setError('Veuillez remplir le mode de paiement (ID carte bancaire).');
        setLoading(false);
        return;
      }
    }
    if (['bank_transfer', 'bank_draft', 'check'].includes(form.detail_type)) {
      if (!String(form.detail.reference || '').trim()) {
        setError('Veuillez remplir le mode de paiement (référence).');
        setLoading(false);
        return;
      }
    }
    if (form.detail_type === 'cash' && form.operation_side === 'credit') {
      if (!String(form.detail.name || '').trim()) {
        setError('Veuillez remplir le mode de paiement (nom de qui donne l\'argent).');
        setLoading(false);
        return;
      }
    }
    if (form.detail_type === 'bank_deposit') {
      if (!String(form.detail.name || '').trim()) {
        setError('Veuillez remplir le mode de paiement (nom / description).');
        setLoading(false);
        return;
      }
    }

    const modeLabel = currentType?.label || '';
    const paymentSuffix = buildPaymentSuffix(modeLabel, form.detail, detailFields);
    const manualExtra = form.operation_name_extra.trim().toLocaleUpperCase('fr-FR');
    const isManualLibelle = form.operation_name === 'TAPEZ MANUELLE';

    if (isManualLibelle && !manualExtra) {
      setError('Saisissez le libellé manuel.');
      setLoading(false);
      return;
    }

    // Libellé final = base + complément manuel + mode + nom/référence
    const suffixParts = [];
    if (manualExtra) suffixParts.push(manualExtra);
    if (paymentSuffix) suffixParts.push(paymentSuffix);

    const payload = {
      client_id: clientId,
      date: form.date,
      operation_side: form.operation_side,
      operation_name: form.operation_name,
      operation_name_suffix: suffixParts.filter(Boolean).join(' ') || null,
      amount: amt,
      detail_type: form.detail_type,
      detail: form.detail,
      actor_username: user?.username || user?.nom || 'Utilisateur',
    };

    try {
      const res = await fetch(
        isEdit ? `/api/charges-mensuelles/${editId}` : '/api/charges-mensuelles',
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: authJsonHeaders(),
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || (isEdit ? 'Erreur lors de la modification' : 'Erreur lors de l\'enregistrement'));
      }
      onSuccess?.(data);
    } catch (err) {
      const msg = err.message || 'Erreur réseau';
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const fieldLabels = {
    name: form.detail_type === 'cash' ? 'Nom (qui donne l\'argent)' : 'Nom / description',
    reference: 'Référence',
    card_id: 'ID carte bancaire',
  };

  // Aperçu du libellé qui sera enregistré
  const libellePreview = useMemo(() => {
    const base = form.operation_name || '';
    if (!base) return '';
    const modeLabel = currentType?.label || '';
    const paymentSuffix = buildPaymentSuffix(modeLabel, form.detail, detailFields);
    const manual = form.operation_name_extra.trim().toLocaleUpperCase('fr-FR');
    if (base === 'TAPEZ MANUELLE') {
      return [manual, paymentSuffix].filter(Boolean).join(' ') || '…';
    }
    return [base, manual, paymentSuffix].filter(Boolean).join(' ');
  }, [form.operation_name, form.operation_name_extra, form.detail, currentType, detailFields]);

  const showLibelleExtra = Boolean(form.operation_name);

  const inputClass = isEdit ? 'coaf-input' : 'form-control';

  const renderDetailFieldStyled = (field) => {
    if (field === 'card_id') {
      return (
        <input
          type="number"
          min="1"
          className={inputClass}
          value={form.detail.card_id || ''}
          onChange={(e) => setDetailField('card_id', e.target.value)}
          placeholder="ID carte (obligatoire)"
          required
        />
      );
    }
    if (field === 'name') {
      return (
        <input
          type="text"
          className={inputClass}
          value={form.detail.name || ''}
          onChange={(e) => setDetailField('name', e.target.value)}
          placeholder={
            form.detail_type === 'cash'
              ? 'Nom de la personne qui donne l\'argent'
              : 'Nom / description'
          }
        />
      );
    }
    if (field === 'reference') {
      return (
        <input
          type="text"
          className={inputClass}
          value={form.detail.reference || ''}
          onChange={(e) => setDetailField('reference', e.target.value)}
          placeholder="Référence"
        />
      );
    }
    return null;
  };

  // ——— Formulaire Ajouter : layout d’origine ———
  if (!isEdit) {
    return (
      <form onSubmit={handleSubmit} className="coaf-form">
        {error && (
          <div className="coaf-alert" role="alert">{error}</div>
        )}

        <div className="coaf-section-title">Type d&apos;opération</div>
        <div className="coaf-side-row">
          <label className={`coaf-chip${form.operation_side === 'credit' ? ' is-on' : ''}`}>
            <input
              type="radio"
              name="operation_side"
              value="credit"
              checked={form.operation_side === 'credit'}
              onChange={() => handleSideChange('credit')}
              disabled={isCarteBancaire}
            />
            Crédit
          </label>
          <label className={`coaf-chip${form.operation_side === 'debit' ? ' is-on' : ''}`}>
            <input
              type="radio"
              name="operation_side"
              value="debit"
              checked={form.operation_side === 'debit'}
              onChange={() => handleSideChange('debit')}
            />
            Débit
          </label>
        </div>

        <div className="coaf-grid">
          <div>
            <label className="form-label">Date</label>
            {isCaissier ? (
              <select
                className="form-select"
                value={getCaissierDateValueForSelect(form.date)}
                onChange={(e) => handleDateChange(e.target.value)}
                required
              >
                {getCaissierDateOptions(annee).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                className="form-control"
                value={form.date}
                onChange={(e) => handleDateChange(e.target.value)}
                required
              />
            )}
          </div>

          <div className="coaf-libelle-block">
            <div className="coaf-libelle-row">
              <select
                className="form-select"
                value={form.operation_name}
                onChange={(e) => setField('operation_name', e.target.value)}
                required
              >
                <option value="">Sélectionner…</option>
                {availableLibelles.map((name) => {
                  const blocked = isPayeParFondLibelle(name) && !canPayFromFond;
                  const allowCurrent = isEdit && isPayeParFondLibelle(form.operation_name) && isPayeParFondLibelle(name);
                  return (
                    <option
                      key={name}
                      value={name}
                      disabled={blocked && !allowCurrent}
                      style={blocked && !allowCurrent ? { color: '#94a3b8' } : undefined}
                    >
                      {blocked && !allowCurrent ? `${name} (fond = 0)` : name}
                    </option>
                  );
                })}
              </select>
              {showLibelleExtra && (
                <input
                  type="text"
                  className="form-control coaf-libelle-extra"
                  value={form.operation_name_extra}
                  onChange={(e) => setField('operation_name_extra', e.target.value)}
                  placeholder={form.operation_name === 'TAPEZ MANUELLE' ? 'Saisir le libellé personnalisé' : '...'}
                  required={form.operation_name === 'TAPEZ MANUELLE'}
                  aria-label="Complément de libellé"
                />
              )}
            </div>
            {!canPayFromFond && form.operation_side === 'debit' && (
              <div className="coaf-hint-fond" role="note">
                « PAYÉ PAR FOND CLIENT » est indisponible tant que le fond client chez CGM est à 0.
              </div>
            )}
            {libellePreview && (
              <div className="coaf-libelle-preview" title="Libellé enregistré">
                <span className="coaf-libelle-preview__label">Libellé enregistré :</span>{' '}
                <strong>{libellePreview}</strong>
              </div>
            )}
          </div>

          <div>
            <label className="form-label">
              Montant ({form.operation_side === 'credit' ? 'crédit' : 'débit'})
            </label>
            <input
              type="number"
              step="0.001"
              className="form-control"
              value={form.amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              placeholder="0.000"
              required
            />
          </div>

          <div>
            <label className="form-label">
              Mode de {form.operation_side === 'credit' ? 'crédit' : 'débit'}
            </label>
            <select
              className="form-select"
              value={form.detail_type}
              onChange={(e) => handleDetailTypeChange(e.target.value)}
              required
            >
              {detailTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {visibleDetailFields.length > 0 && (
          <>
            <div className="coaf-section-title">Détail {currentType?.label || ''}</div>
            <div className="coaf-grid">
              {visibleDetailFields.map((field) => (
                <div key={field}>
                  <label className="form-label">{fieldLabels[field] || field}</label>
                  {renderDetailFieldStyled(field)}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="coaf-actions">
          <button type="submit" className="cc-btn-save" disabled={loading}>
            <i className="fas fa-save" aria-hidden="true" />
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button type="button" className="cc-btn-cancel" onClick={onCancel} disabled={loading}>
            <i className="fas fa-times" aria-hidden="true" /> Annuler
          </button>
        </div>
      </form>
    );
  }

  // ——— Formulaire Modifier (modal compact) ———
  return (
    <form onSubmit={handleSubmit} className="coaf-form coaf-form--edit">
      {error && (
        <div className="coaf-alert" role="alert">{error}</div>
      )}
      {loadingDetail && (
        <div className="coaf-loading">Chargement de l&apos;opération…</div>
      )}

      <fieldset disabled={loadingDetail} className="coaf-fieldset">
        <div className="coaf-side-row" role="radiogroup" aria-label="Type d'opération">
          <button
            type="button"
            className={`coaf-side-btn${form.operation_side === 'credit' ? ' is-on' : ''}`}
            onClick={() => handleSideChange('credit')}
            disabled={isCarteBancaire}
          >
            Crédit
          </button>
          <button
            type="button"
            className={`coaf-side-btn${form.operation_side === 'debit' ? ' is-on' : ''}`}
            onClick={() => handleSideChange('debit')}
          >
            Débit
          </button>
        </div>

        <div className="coaf-grid">
          <div className="coaf-field">
            <label className="coaf-label">Date</label>
            {isCaissier ? (
              <select
                className="coaf-input"
                value={getCaissierDateValueForSelect(form.date)}
                onChange={(e) => handleDateChange(e.target.value)}
                required
              >
                {getCaissierDateOptions(annee).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                className="coaf-input"
                value={form.date}
                onChange={(e) => handleDateChange(e.target.value)}
                required
              />
            )}
          </div>

          <div className="coaf-field">
            <label className="coaf-label">
              Montant ({form.operation_side === 'credit' ? 'crédit' : 'débit'})
            </label>
            <input
              type="number"
              step="0.001"
              className="coaf-input"
              value={form.amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              placeholder="0.000"
              required
            />
          </div>

          <div className="coaf-field coaf-field--full">
            <label className="coaf-label">Libellé</label>
            <div className="coaf-libelle-row">
              <select
                className="coaf-input"
                value={form.operation_name}
                onChange={(e) => setField('operation_name', e.target.value)}
                required
              >
                <option value="">Sélectionner…</option>
                {availableLibelles.map((name) => {
                  const blocked = isPayeParFondLibelle(name) && !canPayFromFond;
                  const allowCurrent = isEdit && isPayeParFondLibelle(form.operation_name) && isPayeParFondLibelle(name);
                  return (
                    <option key={name} value={name} disabled={blocked && !allowCurrent}>
                      {blocked && !allowCurrent ? `${name} (fond = 0)` : name}
                    </option>
                  );
                })}
              </select>
              {showLibelleExtra && (
                <input
                  type="text"
                  className="coaf-input coaf-libelle-extra"
                  value={form.operation_name_extra}
                  onChange={(e) => setField('operation_name_extra', e.target.value)}
                  placeholder={form.operation_name === 'TAPEZ MANUELLE' ? 'Libellé personnalisé' : 'Complément (ex. MOIS JANVIER)'}
                  required={form.operation_name === 'TAPEZ MANUELLE'}
                  aria-label="Complément de libellé"
                />
              )}
            </div>
            {!canPayFromFond && form.operation_side === 'debit' && (
              <div className="coaf-hint-fond" role="note">
                « PAYÉ PAR FOND CLIENT » est indisponible tant que le fond client chez CGM est à 0.
              </div>
            )}
            {libellePreview && (
              <div className="coaf-libelle-preview" title="Libellé enregistré">
                <span className="coaf-libelle-preview__label">Libellé enregistré :</span>{' '}
                <strong>{libellePreview}</strong>
              </div>
            )}
          </div>

          <div className="coaf-field coaf-field--full">
            <label className="coaf-label">
              Mode de {form.operation_side === 'credit' ? 'crédit' : 'débit'}
            </label>
            <select
              className="coaf-input"
              value={form.detail_type}
              onChange={(e) => handleDetailTypeChange(e.target.value)}
              required
            >
              {detailTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          {visibleDetailFields.map((field) => (
            <div key={field} className="coaf-field">
              <label className="coaf-label">{fieldLabels[field] || field}</label>
              {renderDetailFieldStyled(field)}
            </div>
          ))}
        </div>

        <div className="coaf-actions">
          <button type="button" className="coaf-btn coaf-btn--cancel" onClick={onCancel} disabled={loading}>
            Annuler
          </button>
          <button type="submit" className="coaf-btn coaf-btn--save" disabled={loading || loadingDetail}>
            {loading ? 'Modification…' : 'Modifier'}
          </button>
        </div>
      </fieldset>
    </form>
  );
}
